// api.js
import { API } from "../../../backend";
import { objectToQueryString } from "../../../lib/utils";
import { isAuthenticated } from "../../../auth";
import {
  getMembershipPlans,
  getMembershipPlanCount,
  publishUnpublishMembershipPlans
} from "../../membership-plans/helper/api";

/* ==========================================================================
   Membership Offer API
   ========================================================================== */

export const getMembershipOffer = (userId, token, membershipofferId) => {
  return fetch(`${API}/membership-offer/${userId}/${membershipofferId}`, {
    method: "GET",
    headers: {
      "Content-type": "application/json",
      Authorization: `Bearer ${token}`
    }
  })
    .then(response => response.json())
    .catch(err => { console.error(err); return { error: true, message: err?.message }; })
};

export const createMembershipOffer = (userId, token, membershipoffer) => {
  return fetch(`${API}/membership-offer/${userId}`, {
    method: "POST",
    headers: {
      "Content-type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(membershipoffer)
  })
    .then(response => response.json())
    .catch(err => { console.error(err); return { error: true, message: err?.message }; })
};

export const updateMembershipOffer = (userId, token, membershipofferId, membershipoffer) => {
  return fetch(`${API}/update/membership-offer/${userId}/${membershipofferId}`, {
    method: "PUT",
    headers: {
      "Content-type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(membershipoffer)
  })
    .then(response => response.json())
    .catch(err => { console.error(err); return { error: true, message: err?.message }; })
};

export const deleteMembershipOffers = (userId, token, body) => {
  return fetch(`${API}/delete/membership-offers/${userId}`, {
    method: "POST",
    headers: {
      "Content-type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(body)
  })
    .then(response => response.json())
    .catch(err => { console.error(err); return { error: true, message: err?.message }; })
};

export const getMembershipOffers = (userId, token, query) => {
  let str = objectToQueryString(query);
  return fetch(`${API}/membership-offers/${userId}?${str}`, {
    method: "get",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`
    }
  })
    .then(res => res.json())
    .catch(err => { console.error(err); return { error: true, message: err?.message }; })
};

export const getMembershipOfferCount = (userId, token, query) => {
  let str = objectToQueryString(query);
  return fetch(`${API}/membership-offers/count/${userId}?${str}`, {
    method: "get",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`
    }
  })
    .then(res => res.json())
    .catch(err => { console.error(err); return { error: true, message: err?.message }; })
};

/* ==========================================================================
   Membership Plans Helper
   ========================================================================== */

export const mapMembershipPlansToCardItems = (plans = []) => {
  return plans.map(plan => {
    const pkg = (plan.packages && plan.packages[0]) || {};
    const durationLabel =
      pkg.duration != null && pkg.durationType
        ? `${pkg.duration} ${pkg.durationType}${pkg.duration > 1 ? "s" : ""}`
        : "-";

    return {
      id: String(plan.id ?? plan._id),
      name: plan.name ?? "Unnamed Plan",
      duration: durationLabel,
      sessions: pkg.sessions ?? null
    };
  });
};

export const getPublishedMembershipPlanCardItems = async () => {
  const auth = isAuthenticated();
  if (!auth) {
    return { items: [], error: "You must be signed in to load membership plans." };
  }
  const { token, user } = auth;
  const userId = user._id;

  const branchId = localStorage.getItem("branchId");

  const baseQuery = { isPublished: true };
  if (branchId) baseQuery.branchId = branchId;

  try {
    const countRes = await getMembershipPlanCount(userId, token, baseQuery);
    const total =
      countRes?.count ??
      countRes?.data?.count ??
      (typeof countRes === "number" ? countRes : 0) ??
      0;

    const plansRes = await getMembershipPlans(userId, token, {
      ...baseQuery,
      limit: total || 100,
      page: 1
    });

    if (plansRes?.error) {
      throw new Error(plansRes.error);
    }

    const fetchedPlans =
      plansRes?.membershipplans ??
      plansRes?.membershipPlans ??
      plansRes?.data ??
      plansRes?.plans ??
      (Array.isArray(plansRes) ? plansRes : []) ??
      [];

    const items = mapMembershipPlansToCardItems(
      Array.isArray(fetchedPlans) ? fetchedPlans : []
    );

    return { items, error: null };
  } catch (err) {
    console.error("Failed to fetch membership plans:", err);
    return { items: [], error: "Failed to load membership plans. Please try again." };
  }
};

export const unpublishMembershipPlan = async membershipPlanId => {
  const auth = isAuthenticated();
  if (!auth) {
    return { success: false, error: "You must be signed in to update membership plans." };
  }
  const { token, user } = auth;
  const userId = user._id;

  try {
    const res = await publishUnpublishMembershipPlans(userId, token, {
      membership_plan_ids: [membershipPlanId],
      isPublished: false
    });

    if (res?.error) {
      throw new Error(
        typeof res.error === "string" ? res.error : "Failed to unpublish plan."
      );
    }

    return { success: true, error: null };
  } catch (err) {
    console.error("Failed to unpublish membership plan:", err);
    return { success: false, error: "Failed to unpublish membership plan. Please try again." };
  }
};

/* ==========================================================================
   Workout Planner API
   ========================================================================== */

export const getWorkoutPlanner = (userId, token, plannerId) => {
  return fetch(`${API}/workout/planner/${userId}/${plannerId}`, {
    method: "get",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`
    }
  })
    .then(res => res.json())
    .catch(err => { console.error(err); return { error: true, message: err?.message }; })
};

export const getAllWorkoutPlanners = (userId, token, query = {}) => {
  const str = objectToQueryString(query);
  return fetch(`${API}/workout/planners/${userId}?${str}`, {
    method: "get",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`
    }
  })
    .then(res => res.json())
    .catch(err => { console.error(err); return { error: true, message: err?.message }; })
};

export const getWorkoutPlannerCount = (userId, token, query = {}) => {
  const str = objectToQueryString(query);
  return fetch(`${API}/workout/planners/count/${userId}?${str}`, {
    method: "get",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`
    }
  })
    .then(res => res.json())
    .catch(err => { console.error(err); return { error: true, message: err?.message }; })
};

export const getPublishedWorkoutPlanners = (query = {}) => {
  const str = objectToQueryString(query);
  return fetch(`${API}/public/workout/planners?${str}`, {
    method: "get",
    headers: {
      Accept: "application/json"
    }
  })
    .then(res => res.json())
    .catch(err => { console.error(err); return { error: true, message: err?.message }; })
};

export const getPublishedWorkoutPlannerCount = (query = {}) => {
  const str = objectToQueryString(query);
  return fetch(`${API}/public/workout/planners/count?${str}`, {
    method: "get",
    headers: {
      Accept: "application/json"
    }
  })
    .then(res => res.json())
    .catch(err => { console.error(err); return { error: true, message: err?.message }; })
};

export const unpublishWorkoutPlanner = (userId, token, plannerId) => {
  return fetch(`${API}/change/workout/planner/publish-status/${userId}/unpublish`, {
    method: "post",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ plannerIDs: [plannerId] })
  })
    .then(async (res) => {
      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (parseErr) {
        console.error("Non-JSON response:", text);
      }

      if (!res.ok) {
        return { success: false, error: data?.error || data?.message || "Failed to remove planner" };
      }

      // agar backend success flag nahi bhejta, par status ok hai, to manually true maan lo
      return { success: data?.success ?? true, ...data };
    })
    .catch(err => {
      console.error(err);
      return { success: false, error: "Something went wrong. Please try again." };
    });
};